import React from 'react'
import { Teacher, useAppContext } from '../../providers/context'
import { ThreePanelLayout } from '../../layouts/threePanel'
import { CharcoalInput, DelayedTextInput, TextInput } from '../../components/form/textInput'
import { ListButton, ListButtonCheckbox } from '../../components/simple/listButton'
import { laravelValidationToToast, makeRequest, usePaginatedData, useRequest } from '../../request'
import { Divider } from '../../components/simple/divider'
import { Button, ButtonShape, ButtonStyle } from '../../components/simple/button'
import {
  IconCaretDown,
  IconCaretRight,
  IconFileDownload,
  IconFileUpload,
  IconPlus,
  IconSearch,
} from '../../icons/library'
import { Loader } from '../../components/simple/loader'
import { TeacherContact, TeacherContactRow } from './teacherContact'
import { EditTeacherGroupModal, EditTeacherGroupState, NEW_TEACHER_GROUP_STATE, TeacherGroup } from './editTeacherGroup'
import { LabeledCheckbox } from '../../components/form/checkbox'
import { Select } from '../../components/form/select'
import { UploadedFile, FileUploadRow } from '../../components/media/fileUpload'
import { ControlledModal } from '../../layouts/components/modal'
import { Badge, BadgeStyle } from '../../components/simple/badge'
import { simpleRangeOptions } from '../../components/form/datePicker'
import { Popups } from '../../providers/popups'

const sortOptions = [
  { name: 'Name', value: 'name' },
  { name: 'Logins', value: 'logins' },
  { name: 'Usage', value: 'usage' },
]

export const TeacherContactsPage: React.FC<{
  user: Teacher
}> = ({ user }) => {
  const [search, setSearch] = React.useState('')
  const [sort, setSort] = React.useState(sortOptions[0])
  const [timerange, setTimerange] = React.useState(simpleRangeOptions[0])

  const [extraFiltersOpen, setExtraFiltersOpen] = React.useState(false)

  const [filteredSchools, setFilteredSchools] = React.useState(new Set<number>())
  const [onlyTeachersWithClasses, setOnlyTeachersWithClasses] = React.useState(false)
  const [onlyShowAdmins, setOnlyShowAdmins] = React.useState(false)
  const [onlyShowManuallyAdded, setOnlyShowManuallyAdded] = React.useState(false)
  const [showDeleted, setShowDeleted] = React.useState(false)
  const [onlyExternalStaff, setOnlyExternalStaff] = React.useState(false)
  const [onlyGradeGroupAccess, setOnlyGradeGroupAccess] = React.useState(false)
  const [onlyAllClassAccess, setOnlyAllClassAccess] = React.useState(false)
  const [onlyPTCAdminAccess, setOnlyPTCAdminAccess] = React.useState(false)
  const [onlyAssessmentAdminAccess, setOnlyAssessmentAdminAccess] = React.useState(false)
  const [onlyLexikeetAdminAccess, setOnlyLexikeetAdminAccess] = React.useState(false)
  const [onlyAttendanceAccess, setOnlyAttendanceAccess] = React.useState(false)
  const [onlyBroadcastAdminAccess, setOnlyBroadcastAdminAccess] = React.useState(false)
  const [onlyNonAdmins, setOnlyNonAdmins] = React.useState(false)
  const [onlyDistrictAccess, setOnlyDistrictAccess] = React.useState(false)

  const [showNewTeacherModal, setShowNewTeacherModal] = React.useState(false)
  const [showCSVImportModal, setShowCSVImportModal] = React.useState(false)
  const [editTeacherGroupState, setEditTeacherGroupState] = React.useState<EditTeacherGroupState | undefined>(undefined)

  const { data: teacherGroups, reload: reloadTeacherGroups } = useRequest<TeacherGroup[]>(
    '/frontend_api/getTeacherGroups'
  )

  const contactPostData = React.useMemo(
    () => ({
      search,
      schools: filteredSchools,
      onlyTeachersWithClasses,
      onlyShowAdmins,
      onlyShowManuallyAdded,
      showDeleted,
      onlyExternalStaff,
      onlyGradeGroupAccess,
      onlyAllClassAccess,
      onlyPTCAdminAccess,
      onlyAssessmentAdminAccess,
      onlyLexikeetAdminAccess,
      onlyAttendanceAccess,
      onlyBroadcastAdminAccess,
      onlyNonAdmins,
      onlyDistrictAccess,
      sort: sort.value,
      timerange: timerange.value,
    }),
    [
      search,
      sort,
      filteredSchools,
      onlyTeachersWithClasses,
      onlyShowAdmins,
      onlyShowManuallyAdded,
      showDeleted,
      onlyExternalStaff,
      onlyGradeGroupAccess,
      onlyAllClassAccess,
      onlyPTCAdminAccess,
      onlyAssessmentAdminAccess,
      onlyLexikeetAdminAccess,
      onlyAttendanceAccess,
      onlyBroadcastAdminAccess,
      onlyNonAdmins,
      onlyDistrictAccess,
      timerange,
    ]
  )
  const {
    values: contacts,
    loadingMore,
    loading,
    loadMore,
    reload: reloadContacts,
  } = usePaginatedData<TeacherContact, typeof contactPostData>('/frontend_api/getTeacherContacts', contactPostData)

  const extraFiltersActive =
    (onlyGradeGroupAccess ? 1 : 0) +
    (onlyAllClassAccess ? 1 : 0) +
    (onlyAttendanceAccess ? 1 : 0) +
    (onlyPTCAdminAccess ? 1 : 0) +
    (onlyLexikeetAdminAccess ? 1 : 0) +
    (onlyBroadcastAdminAccess ? 1 : 0) +
    (onlyNonAdmins ? 1 : 0) +
    (onlyDistrictAccess ? 1 : 0)

  return (
    <ThreePanelLayout
      noMobile
      user={user}
      left={
        <>
          <DelayedTextInput
            className={`rounded-full outline outline-1 outline-charcoal-text ${CharcoalInput}`}
            value={search}
            onChange={setSearch}
            placeholder="Search"
            icon={<IconSearch />}
          />
          <ListButton onClick={() => setOnlyTeachersWithClasses((c) => !c)}>
            Only show teachers with classes / groups
            <ListButtonCheckbox checked={onlyTeachersWithClasses} />
          </ListButton>
          {(user.schools.length > 1 || (user.districtAccess && user.district.schools.length > 1)) && (
            <>
              <Divider className="!border-charcoal-text" />
              <div className="flex flex-col gap-1">
                <div className="ml-2 mt-2 font-bold">Filter Schools</div>
                {(user.districtAccess ? user.district.schools : user.schools).sort().map((school) => (
                  <ListButton
                    key={school.id}
                    onClick={() => {
                      const schools = new Set(filteredSchools)
                      if (!schools.has(school.id)) schools.add(school.id)
                      else schools.delete(school.id)
                      setFilteredSchools(schools)
                    }}
                  >
                    {school.name}
                    <ListButtonCheckbox checked={filteredSchools.has(school.id)} />
                  </ListButton>
                ))}
              </div>
            </>
          )}
          <Divider className="!border-charcoal-text" />
          <div className="flex flex-col gap-1">
            <ListButton onClick={() => setOnlyShowAdmins((c) => !c)}>
              Only show admins
              <ListButtonCheckbox checked={onlyShowAdmins} />
            </ListButton>
            <ListButton onClick={() => setOnlyShowManuallyAdded((c) => !c)}>
              Only show manually added users
              <ListButtonCheckbox checked={onlyShowManuallyAdded} />
            </ListButton>
            <ListButton onClick={() => setShowDeleted((c) => !c)}>
              Show deleted users
              <ListButtonCheckbox checked={showDeleted} />
            </ListButton>
            <ListButton onClick={() => setOnlyExternalStaff((c) => !c)}>
              Only show external staff
              <ListButtonCheckbox checked={onlyExternalStaff} />
            </ListButton>
            <div className="ml-2 flex cursor-pointer items-center gap-2" onClick={() => setExtraFiltersOpen((c) => !c)}>
              {extraFiltersOpen ? <IconCaretDown /> : <IconCaretRight />} More Filters{' '}
              {!extraFiltersOpen && extraFiltersActive > 0 && (
                <Badge badgeStyle={BadgeStyle.warning}>{extraFiltersActive}</Badge>
              )}
            </div>
            {extraFiltersOpen && (
              <>
                <div className="ml-2 mt-2 flex flex-row items-center justify-between gap-2">
                  Sort by:
                  <Select
                    className="max-w-40 !border-none bg-charcoal-active !ring-0"
                    value={sort}
                    options={sortOptions}
                    onChange={setSort}
                  />
                </div>
                <div className="ml-2 flex flex-row items-center justify-between gap-2">
                  Stats for:
                  <Select
                    className="max-w-40 !border-none bg-charcoal-active !ring-0"
                    value={timerange}
                    options={simpleRangeOptions}
                    onChange={setTimerange}
                  />
                </div>
                <ListButton onClick={() => setOnlyGradeGroupAccess((c) => !c)}>
                  Only staff with grade group access
                  <ListButtonCheckbox checked={onlyGradeGroupAccess} />
                </ListButton>
                <ListButton onClick={() => setOnlyAllClassAccess((c) => !c)}>
                  Only staff with all class access
                  <ListButtonCheckbox checked={onlyAllClassAccess} />
                </ListButton>
                {user.district.attendanceEnabled && (
                  <ListButton onClick={() => setOnlyAttendanceAccess((c) => !c)}>
                    Only attendance access
                    <ListButtonCheckbox checked={onlyAttendanceAccess} />
                  </ListButton>
                )}
                {user.district.ptcEnabled && (
                  <ListButton onClick={() => setOnlyPTCAdminAccess((c) => !c)}>
                    Only PTC admin access
                    <ListButtonCheckbox checked={onlyPTCAdminAccess} />
                  </ListButton>
                )}
                {user.district.assessmentsEnabled && (
                  <ListButton onClick={() => setOnlyAssessmentAdminAccess((c) => !c)}>
                    Only document access
                    <ListButtonCheckbox checked={onlyAssessmentAdminAccess} />
                  </ListButton>
                )}
                {user.district.lexikeetForAdminOnly && (
                  <ListButton onClick={() => setOnlyLexikeetAdminAccess((c) => !c)}>
                    Only Lexikeet admin access
                    <ListButtonCheckbox checked={onlyLexikeetAdminAccess} />
                  </ListButton>
                )}
                {user.district.massCallingEnabled && (
                  <ListButton onClick={() => setOnlyBroadcastAdminAccess((c) => !c)}>
                    Only broadcast admin access
                    <ListButtonCheckbox checked={onlyBroadcastAdminAccess} />
                  </ListButton>
                )}
                <ListButton onClick={() => setOnlyNonAdmins((c) => !c)}>
                  Only non-admins
                  <ListButtonCheckbox checked={onlyNonAdmins} />
                </ListButton>
                <ListButton onClick={() => setOnlyDistrictAccess((c) => !c)}>
                  Only district access
                  <ListButtonCheckbox checked={onlyDistrictAccess} />
                </ListButton>
              </>
            )}
          </div>
          <Divider className="!border-charcoal-text" />
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold">Teacher Groups</span>
              {!user.externalStaff && (
                <Button
                  shape={ButtonShape.roundedSmall}
                  logo
                  onClick={() => setEditTeacherGroupState(NEW_TEACHER_GROUP_STATE)}
                >
                  New
                </Button>
              )}
            </div>
            {(teacherGroups || []).map((group) => (
              <ListButton
                key={group.id}
                subMenu={
                  <>
                    {group.teachers.map((teacher) => (
                      <ListButton key={teacher.id} thin>
                        {teacher.name}
                      </ListButton>
                    ))}
                    <ListButton
                      thin
                      buttonStyle={ButtonStyle.ghostWarning}
                      onClick={() => setEditTeacherGroupState({ group })}
                    >
                      Edit
                    </ListButton>
                  </>
                }
              >
                {group.name}
              </ListButton>
            ))}
          </div>
        </>
      }
      bottomLeft={
        user.allowedToAddTeachers && (
          <>
            <Button
              tooltip="Add an individual teacher"
              logo
              shape={ButtonShape.iconCircleLarge}
              onClick={() => setShowNewTeacherModal(true)}
            >
              <IconPlus size="lg" />
            </Button>
            {user.admin === 2 && (user.districtAccess || user.district.allow_all_to_add_users) && (
              <>
                <Button
                  buttonStyle={ButtonStyle.dark}
                  shape={ButtonShape.iconCircleLarge}
                  onClick={() => setShowCSVImportModal(true)}
                  tooltip="Import teachers from CSV"
                >
                  <IconFileUpload size="lg" />
                </Button>
                <Button
                  buttonStyle={ButtonStyle.dark}
                  shape={ButtonShape.iconCircleLarge}
                  link="/csvExportTeachers"
                  tooltip="Export teachers as CSV"
                >
                  <IconFileDownload size="lg" />
                </Button>
              </>
            )}
          </>
        )
      }
      floating={
        <>
          {showNewTeacherModal && (
            <NewTeacherModal
              user={user}
              onClose={() => {
                setShowNewTeacherModal(false)
                reloadContacts()
              }}
            />
          )}
          {showCSVImportModal && <CSVImportTeacherModal user={user} onClose={() => setShowCSVImportModal(false)} />}
          {editTeacherGroupState && (
            <EditTeacherGroupModal
              editTeacherGroupState={editTeacherGroupState}
              setEditTeacherGroupState={setEditTeacherGroupState}
              user={user}
              onSaved={reloadTeacherGroups}
            />
          )}
        </>
      }
    >
      {!contacts || (contacts.length === 0 && loading) ? (
        <Loader />
      ) : (
        <div
          className={`flex h-full flex-col items-start gap-3 overflow-auto bg-lightBlue p-3 ${loading ? 'pointer-events-none opacity-50' : ''}`}
        >
          {contacts.map((contact) => (
            <TeacherContactRow key={contact.id} teacher={contact} reloadContacts={reloadContacts} user={user} />
          ))}
          {loadMore && !loading && (
            <Button className="mx-auto my-3" logo small onClick={loadMore} disabled={loadingMore}>
              Load More
            </Button>
          )}
        </div>
      )}
    </ThreePanelLayout>
  )
}

const NewTeacherModal: React.FC<{ user: Teacher; onClose: () => void }> = ({ user, onClose }) => {
  const [firstName, setFirstName] = React.useState('')
  const [lastName, setLastName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [phoneNumber, setPhoneNumber] = React.useState('')

  const schools = React.useMemo(
    () =>
      (user.districtAccess ? [...user.district.schools, { name: 'District Access', id: '' }] : user.schools).map(
        (s) => ({
          name: s.name,
          value: s.id,
        })
      ),
    [user]
  )
  const [school, setSchool] = React.useState(schools[0])
  const [admin, setAdmin] = React.useState(false)
  const [externalStaff, setExternalStaff] = React.useState(false)

  const [loading, setLoading] = React.useState(false)

  return (
    <ControlledModal
      open={true}
      onOpenChange={(c) => {
        if (!c) onClose()
      }}
      title={`Add A Teacher To Your ${user.districtAccess ? 'District' : 'School'}`}
      disabled={loading}
      showLoader={loading}
      size="sm"
    >
      <TextInput placeholder="First Name" value={firstName} onChange={setFirstName} blue />
      <TextInput placeholder="Last Name" value={lastName} onChange={setLastName} blue />
      <TextInput placeholder="Email" value={email} onChange={setEmail} blue validation="email" />
      <TextInput placeholder="Phone Number" value={phoneNumber} onChange={setPhoneNumber} blue validation="phone" />
      {schools.length > 1 && <Select options={schools} value={school} onChange={setSchool} blue />}
      {user.admin === 2 && <LabeledCheckbox label="Admin" checked={admin} onChecked={setAdmin} labelBefore />}
      <LabeledCheckbox
        label="External Staff / Coach"
        checked={externalStaff}
        onChecked={setExternalStaff}
        labelBefore
      />
      <Button
        disabled={loading || firstName.trim() === '' || lastName.trim() === '' || email.trim() === ''}
        className="mt-3 self-start"
        logo
        onClick={() => {
          setLoading(true)
          makeRequest('/teachers/new', {
            fname: firstName,
            lname: lastName,
            phoneNumber,
            email,
            school: school.value,
            admin,
            nonTeacher: externalStaff,
          })
            .then(onClose)
            .catch((e) => laravelValidationToToast('Failed to add this teacher.', e))
            .finally(() => setLoading(false))
        }}
      >
        Import
      </Button>
    </ControlledModal>
  )
}

const CSVImportTeacherModal: React.FC<{ user: Teacher; onClose: () => void }> = ({ user, onClose }) => {
  const { state } = useAppContext()
  const [loading, setLoading] = React.useState(false)
  const [uploadedFiles, setUploadedFiles] = React.useState<UploadedFile[]>([])
  const importType = user.district.csvImport ? 'teachers' : 'external staff'
  return (
    <ControlledModal
      open={true}
      onOpenChange={(c) => {
        if (!c) onClose()
      }}
      title="Import Teachers From CSV"
      size="sm"
      disabled={loading}
      showLoader={loading}
    >
      <b className="text-lg">Import staff from Google Spreadsheet</b>
      <div>
        You can open a spreadsheet of your staff by clicking these links. You can choose to{' '}
        <a
          className="text-link"
          onClick={(e) => {
            e.preventDefault()
            Popups.confirm({
              message: "Are you sure you want to replace your spreadsheet's data with the user data on ReachMyTeach?",
              yesText: 'Yes',
              noText: 'No',
            }).then((confirmed) => {
              if (confirmed) {
                window.open('/spreadsheet/update?type=staff', '_blank')
              }
            })
          }}
          href="/spreadsheet/update?type=staff"
        >
          click here to update spreadsheet
        </a>{' '}
        with the current info in the system, or{' '}
        <Button
          buttonStyle={ButtonStyle.link}
          shape={ButtonShape.inlineSmall}
          openInNewTab
          link="/spreadsheet/open?type=staff"
        >
          open spreadsheet
        </Button>
      </div>
      <b>To import those changes, you first will need to validate them:</b>

      <form method="post" action="/csvImport" className="flex flex-col gap-2">
        <input type="hidden" name="_token" value={state.csrf} />
        <input type="hidden" name="spreadsheet" value="staff" />
        <div className="flex items-center gap-2">
          <input type="checkbox" name="deactivate" />
          <label>Deactivate {importType} not in this file</label>
        </div>
        <button
          className={`${ButtonStyle.logo} ${ButtonShape.standard} self-start`}
          name="action"
          value="Test"
          type="submit"
          onClick={() => setLoading(true)}
        >
          Validate
        </button>
      </form>
      <Divider>OR</Divider>
      <b className="text-lg">Import directly</b>
      <FileUploadRow maxFiles={1} uploadedFiles={uploadedFiles} setUploadedFiles={setUploadedFiles}>
        <Button>Upload File</Button>
      </FileUploadRow>
      <div className="text-sm">
        {user.district.csvImport
          ? user.district.schools.length === 1
            ? `This will, by default, import teachers to be at your school: ${user.district.schools[0].name} You can add a column external if you would like to import them as external staff (y/n). You can also add a column school name if you would like to import them at a different school.`
            : 'This will import teachers to be at your district. You will need to have a column School Name. You can also add a column external if you would like to import them as external staff (y/n).'
          : 'This will, by default, import external teachers to be at your district. You can add a column external if you would like to import them as regular teachers (y/n). You can also add a column school name if you would like to import them at a different school.'}{' '}
        <a
          href="https://reachmyteach.zendesk.com/hc/en-us/articles/16723145069465-Adding-Viewing-External-Contacts"
          target="_blank"
          className="text-link"
        >
          Help
        </a>
      </div>
      <form method="post" action="/csvImport" className="flex flex-col gap-2">
        <input type="hidden" name="_token" value={state.csrf} />
        <input
          type="hidden"
          name="filenameteachers"
          value={uploadedFiles.length > 0 ? uploadedFiles[0].fileName : ''}
        />
        <div className="flex items-center gap-2">
          <input type="checkbox" name="deactivate" />
          <label>Deactivate {importType} not in this file</label>
        </div>
        <button
          className={`${ButtonStyle.logo} ${ButtonShape.standard} self-start ${uploadedFiles.length === 0 ? 'opacity-50' : ''}`}
          name="action"
          value="Test"
          type="submit"
          onClick={() => setLoading(true)}
          disabled={uploadedFiles.length === 0}
        >
          Validate
        </button>
      </form>
      <a href="/csvExportTeachers?justCSV=1" target="_blank" className="text-link">
        Download current {importType}
      </a>
    </ControlledModal>
  )
}
